import React from 'react';
import cuid from 'cuid';
import { useSWRConfig } from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import {
  removeObjectFromArrayByKey,
  updateObjectInArray,
} from '@/utils/arrayUtils';
import { useTasksContext } from '../components/TasksProvider';
import { getAPIDataFromForm } from './taskFormUtils';
import { createTask, deleteTask, updateTask } from './tasksServices';

/**
 * To mutate a task list, we need to get the path used to make that call. That
 * path can involve multiple query params, which for some actions, can be
 * accessed by the parent <TasksProvider>. However, some instances, such as
 * <EntityDetails> do not have access to <TasksProvider>. To get around that
 * limitation, the swr cache is looked at to find the most recent GET call to
 * /tasks.
 */
// eslint-disable-next-line consistent-return
function getMostRecentGETTasksPath(swrCache) {
  const items = Array.from(swrCache.keys());

  // The cache stores the keys in the order that they are received, appending
  // new entries to the end. Loop over the items, going backwards. The first
  // match is going to be the most recent call to /tasks.
  for (let i = items.length - 1; i > 0; i -= 1) {
    const apiPath = items[i];

    if (apiPath.match(/tasks\?/)) {
      return apiPath;
    }
  }
}

export function addTaskToList(currentData, newTask, { page, pageSize } = {}) {
  const { tasks = [], totalHits = 0 } = currentData;
  const updatedTotalHits = totalHits + 1;

  // We only want to mutate the tasks list if we are on page 1. New tasks are
  // added to the top of the list. On any other page, we'll leave tasks as-is
  // but we do want to update the total hits.
  if (typeof page === 'number' && page !== 1) {
    return {
      ...currentData,
      totalHits: updatedTotalHits,
    };
  }

  // If we're on the first page and we have a full list, we want to show the
  // new task at the top. But we want to remove the last item so that we can
  // make sure that we still render the number of items that match the page
  // size.
  if (page === 1 && tasks.length === pageSize) {
    tasks.pop();
  }

  return {
    ...currentData,
    tasks: [
      {
        ...newTask,
        // use cuid to temporarily add a task id, potentially preventing
        // the task list from throwing a console error when it renders
        // the new task.
        taskId: newTask.taskId || cuid(),
      },
      ...(currentData?.tasks || []),
    ],
    totalHits: updatedTotalHits,
  };
}

export function removeTaskFromList(currentData, taskId) {
  return {
    ...currentData,
    tasks: removeObjectFromArrayByKey(
      currentData?.tasks || [],
      taskId,
      'taskId',
    ),
    totalHits: (currentData?.totalHits || 1) - 1,
  };
}

const useTaskServices = () => {
  const { mutate, cache: swrCache } = useSWRConfig();
  const ownerOrgId = useOwnerOrgId();
  const { user = {} } = useCurrentUser();
  const { userId, displayName } = user;
  const tasksContext = useTasksContext();

  // It is possible that this hook is used and tasks context is not available.
  // That would happen when creating a task from the FAB on any page that isn't
  // the tasks list.
  const hasTasksContext = !!tasksContext;
  const { key, query } = tasksContext || {};
  const { page, pageSize } = query || {};

  const createTasksMutate = React.useCallback(
    async (form, assignees) => {
      const newTask = {
        createdBy: {
          userId,
          displayName,
        },
        ...getAPIDataFromForm({
          assignees,
          form,
          ownerOrgId,
        }),
      };

      if (hasTasksContext) {
        mutate(
          key,
          (currentData = {}) =>
            addTaskToList(currentData, newTask, {
              page,
              pageSize,
            }),
          { revalidate: false },
        );
      }

      const results = await createTask(newTask);

      if (hasTasksContext) {
        // Update the task with the actual data from the UI
        mutate(
          key,
          (currentData = {}) => {
            const { tasks } = currentData;

            return {
              ...currentData,
              // Replace the newly added item which is a placeholder.
              tasks: [results.data.task, ...tasks.slice(1)],
            };
          },
          { revalidate: false },
        );
      }
    },
    [key],
  );

  const updateTaskMutate = React.useCallback(
    async (task, form, assignees) => {
      let updatedTask = {
        updatedBy: {
          userId,
          displayName,
        },
        ...task,
      };
      if (form && assignees) {
        updatedTask = {
          taskId: task.taskId,
          updatedBy: {
            userId,
            displayName,
          },
          ...getAPIDataFromForm({
            assignees,
            form,
            ownerOrgId,
          }),
        };
      }

      mutate(
        key,
        (currentData = {}) => {
          return {
            ...currentData,
            tasks: updateObjectInArray(
              currentData?.tasks || [],
              updatedTask,
              'taskId',
            ),
          };
        },
        { revalidate: false },
      );

      mutate(
        `tasks/${task.taskId}`,
        (currentData = {}) => ({
          ...currentData,
          ...updatedTask,
        }),
        { revalidate: false },
      );

      await updateTask(updatedTask);

      // Only revalidate "tasks/:taskId". If a revalidation of "tasks"
      // is trigger, it is possible that GET /tasks is not fully synced
      // up with the PUT /tasks/:taskId change. This can result in the
      // tasks list rendering with old data.
      mutate(`tasks/${task.taskId}`);
    },
    [key],
  );

  const deleteTaskMutate = React.useCallback(
    async (taskId) => {
      const mostRecentGETTasksPath = getMostRecentGETTasksPath(swrCache);

      if (!mostRecentGETTasksPath?.length) {
        return;
      }

      mutate(
        mostRecentGETTasksPath,
        (currentData = {}) => removeTaskFromList(currentData, taskId),
        { revalidate: false },
      );

      await deleteTask(taskId, ownerOrgId);
    },
    [ownerOrgId],
  );

  return {
    createTask: createTasksMutate,
    deleteTask: deleteTaskMutate,
    updateTask: updateTaskMutate,
  };
};
export default useTaskServices;
